<template>
  <settings-modal class="profile-statement-download-modal">
    <template slot="heading">Please, specify the time period, account and currency for which you want to download the statement</template>

    <v-alert
      v-if="isErrorAlertVisible"
      class="t-error-message"
      :value="true"
      type="error"
    >
      <span v-if="hasProfileAccountsError">Unable to load account information</span>
      <span v-else-if="isInvalidDateRangeSubmitted">Invalid date range</span>
    </v-alert>

    <v-layout row>
      <v-flex xs10>
        <date-range-picker
          :from.sync="filters.dateFrom"
          :to.sync="filters.dateTill"
          :max="today"
          prepend-icon="event"
        />
      </v-flex>
    </v-layout>

    <v-layout row>
      <v-flex xs5>
        <v-select
          v-model="filters.accountId"
          :items="accountOptions"
          :disabled="hasSingleAccountOption"
          :loading="isLoading"
          item-text="accountId"
          item-value="accountId"
          label="Account"
          prepend-icon="work"
        />
      </v-flex>

      <v-flex xs5>
        <v-select
          v-model="filters.currency"
          :items="currencyOptions"
          :disabled="hasSingleCurrencyOption"
          label="Currency"
          prepend-icon="local_atm"
        />
      </v-flex>
    </v-layout>

    <template slot="actions">
      <v-btn
        v-for="fileType in fileTypes"
        :key="fileType"
        :class="`font-weight-bold t-${fileType}-download-button`"
        color="primary"
        medium
        flat
        :disabled="isDownloadButtonDisabled"
        v-bind="isDownloadButtonDisabled || isInvalidDateRangeSelected ? undefined : {
          href: generateDownloadUrl(fileType),
          target: '_blank'
        }"
        @click="isInvalidDateRangeSelected ? onDownloadButtonClick($event) : undefined"
      >download .{{ fileType }}</v-btn>
    </template>
  </settings-modal>
</template>

<script>
  import { mapGetters } from 'vuex'
  import { AppConfig } from '@/config/config'

  // Utils
  import { isEmptyValue } from '@/utils/Data'
  import { formatDate } from '@/utils/Date'

  // Services
  import apiClient from '@/services/AccountClient'
  import ProfileApi, { ProfileAccountsApiPayload } from '@/services/Api/ProfileApi'

  // Components
  import SettingsModal from '@/components/modals/SettingsModal'
  import DateRangePicker from '@/components/ui/DateRangePicker'
  import ApiRequest from '@/services/ApiRequests/ApiRequest'

  export default {
    name: 'ProfileStatementDownloadModal',
    components: {
      SettingsModal,
      DateRangePicker
    },
    data () {
      return {
        fileTypes: ['pdf', 'csv', 'xls'],
        profileAccounts: [],
        isInvalidDateRangeSubmitted: false,
        profileAccountsApiRequest: new ApiRequest(ProfileApi.fetchAccounts, ProfileAccountsApiPayload),
        filters: {
          dateFrom: '',
          dateTill: '',
          accountId: '',
          currency: ''
        }
      }
    },
    computed: {
      ...mapGetters('modal', ['modal']),
      profileId () {
        return this.modal.data.profileId
      },
      today () {
        return formatDate(new Date())
      },
      selectedAccount () {
        return this.profileAccounts.find(({ accountId }) => accountId === this.filters.accountId)
      },
      accountOptions () {
        return this.profileAccounts
      },
      currencyOptions () {
        return this.selectedAccount != null
          ? this.selectedAccount.balances.map(({ currency }) => currency)
          : []
      },
      isLoading () {
        return this.profileAccountsApiRequest.loading
      },
      isErrorAlertVisible () {
        return this.hasProfileAccountsError || this.isInvalidDateRangeSubmitted
      },
      isInvalidDateRangeSelected () {
        return [this.filters.dateFrom, this.filters.dateTill].some(isEmptyValue) || this.filters.dateFrom > this.filters.dateTill
      },
      isDownloadButtonDisabled () {
        return Object.values(this.filters).some(isEmptyValue)
      },
      hasProfileAccountsError () {
        return this.profileAccountsApiRequest.error != null
      },
      hasSingleAccountOption () {
        return this.accountOptions.length === 1
      },
      hasSingleCurrencyOption () {
        return this.currencyOptions.length === 1
      }
    },
    watch: {
      'filters.accountId' () {
        if (this.hasSingleCurrencyOption) {
          this.setFilterValue('currency', this.currencyOptions[0])
        }
      },
      'filters.dateFrom' () {
        this.setIsInvalidDateRangeSubmitted(false)
      },
      'filters.dateTill' () {
        this.setIsInvalidDateRangeSubmitted(false)
      }
    },
    created () {
      this.profileAccountsApiRequest.execute(this.profileId, { size: 100 })
        .then(this.setProfileAccounts)
        .then(() => {
          if (this.hasSingleAccountOption) {
            this.setFilterValue('accountId', this.accountOptions[0].accountId)
          }
        })
        .catch(() => {}) // Error handled in computed prop.
    },
    methods: {
      onDownloadButtonClick () {
        if (this.isInvalidDateRangeSelected) {
          return this.setIsInvalidDateRangeSubmitted(true)
        }
      },
      generateDownloadUrl (fileType) {
        const params = apiClient.serializeParams(this.filters).toString()

        return `${AppConfig.GATEWAY_ACCOUNT_URL}/api/admin/v1/export/statements/${fileType === 'xls' ? 'excel' : fileType}?${params}`
      },
      setProfileAccounts (profileAccounts) {
        this.$set(this, 'profileAccounts', profileAccounts)
      },
      setFilterValue (filterName, filtersValue) {
        if (this.filters.hasOwnProperty(filterName)) {
          this.$set(this.filters, filterName, filtersValue)
        }
      },
      setIsInvalidDateRangeSubmitted (isInvalidDateRangeSubmitted) {
        this.$set(this, 'isInvalidDateRangeSubmitted', isInvalidDateRangeSubmitted)
      }
    }
  }
</script>

<style lang="scss">
  .profile-statement-download-modal {
    &__subheading {
      color: #212121;
      font-size: 16px;
      font-weight: 400;
      padding-top: 8px;
      margin-bottom: 3px;
    }
  }
</style>
